import { useState } from 'react';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { SETTINGS_PAGE } from './settings-shared';
import { useNotificationSettings, useUpdateNotificationSettings } from './use-notification-settings';

/**
 * "Show my name on designs I share" (Recipient field 42, `sharing_settings.show_name_on_shared_designs`).
 * When ON, the public Wixel viewer of a design Symphony made says "Made by <name>'s Symphony assistant", where
 * <name> is the account's business name, else the person's first name. The name is resolved when the page
 * renders (SymphonyBff.GetSharedDesignAttribution) and never stored on the shared asset, so OFF also un-names
 * designs already shared.
 *
 * The same contract as iOS `SettingsScreen.shareNameCard` and Android's preferences card, and the same shape
 * as `useInboxLearning`: the value rides the notification-settings read (unset = ON), the write is a PATCH
 * carrying only this field, and the switch is optimistic — it flips at once and goes back, with the save
 * error, if the write fails.
 */
export function useShareName() {
  const { data } = useNotificationSettings();
  const update = useUpdateNotificationSettings();
  const { logPageClickBi } = useGenericBi();
  const [pending, setPending] = useState<boolean | null>(null);
  const [saveFailed, setSaveFailed] = useState(false);

  const stored = data?.showNameOnSharedDesigns !== false;
  const enabled = pending ?? stored;

  const setEnabled = (next: boolean) => {
    logPageClickBi({ ...SETTINGS_PAGE, button_name: next ? 'enable_share_name' : 'disable_share_name' });
    setPending(next);
    update.mutate(
      { showNameOnSharedDesigns: next },
      {
        onSuccess: () => setPending(null),
        onError: () => {
          setPending(null);
          setSaveFailed(true);
        },
      },
    );
  };

  return { enabled, setEnabled, saveFailed, dismissSaveFailed: () => setSaveFailed(false) };
}
